// ============================================
// PAGE - Register (alianza con partner, genérica)
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignupInitiate } from '@almacenes/core';
import { Button, cn } from '@almacenes/ui';
import logo from '@/assets/logos/nuevo.svg';
import { useRutFormatter } from '@/hooks/useRutFormatter';
import type { AlianzaConfig } from './alianzas.config';
import { BENEFICIOS } from './beneficios';

interface AlianzaRegisterPageProps {
  partner: AlianzaConfig;
}

export const AlianzaRegisterPage = ({ partner }: AlianzaRegisterPageProps) => {
  const navigate = useNavigate();
  const { mutate: signupInitiate, isPending } = useSignupInitiate();

  const {
    rawValue: rutNegocio,
    formattedValue: formattedRutNegocio,
    handleChange: handleRutNegocioChange,
    isValid: isRutNegocioValid,
  } = useRutFormatter();

  const {
    rawValue: run,
    formattedValue: formattedRun,
    handleChange: handleRunChange,
    isValid: isRunValid,
  } = useRutFormatter();

  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    email: '',
    emailConfirm: '',
    telefono: '',
  });

  const [errors, setErrors] = useState({
    rutNegocio: '',
    run: '',
    email: '',
    telefono: '',
  });

  const [apiError, setApiError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    let hasErrors = false;
    const newErrors = { rutNegocio: '', run: '', email: '', telefono: '' };

    if (!isRutNegocioValid()) {
      newErrors.rutNegocio = 'El RUT del negocio no es válido';
      hasErrors = true;
    }

    if (!isRunValid()) {
      newErrors.run = 'El RUN personal no es válido';
      hasErrors = true;
    }

    if (formData.email !== formData.emailConfirm) {
      newErrors.email = 'Los correos electrónicos no coinciden';
      hasErrors = true;
    }

    if (formData.telefono.length !== 9) {
      newErrors.telefono = 'El número de teléfono debe tener 9 dígitos';
      hasErrors = true;
    }

    setErrors(newErrors);
    if (hasErrors) return;

    signupInitiate(
      {
        rutAlmacen: rutNegocio,
        runPersonal: run,
        nombrePropietario: formData.nombre,
        apellidosPropietario: formData.apellidos,
        email: formData.email,
        telefono: parseInt(formData.telefono, 10),
        medioInformacion: partner.medioInformacion,
      },
      {
        onSuccess: () => {
          navigate('/auth/register-success');
        },
        onError: (error: any) => {
          if (error?.code === 'BUSINESS_RULE_VIOLATION') {
            setApiError(error.message);
          } else {
            setApiError(error?.message || 'Error al registrar. Por favor, intenta nuevamente.');
          }
        },
      }
    );
  };

  const nameClassName = partner.nameClassName ?? 'text-blue-600';
  const mesesLabel = partner.monthsTrial === 1 ? 'mes' : 'meses';

  return (
    <div className="min-h-screen h-screen flex flex-col overflow-hidden">
      <div className="flex-1 grid lg:grid-cols-2 h-full">

        {/* Columna izquierda - Información */}
        <div
          className="hidden lg:flex flex-col justify-between p-8 h-full overflow-auto"
          style={{ background: 'linear-gradient(135deg, #FFE27A 0%, #F9D949 100%)' }}
        >
          {/* Header con logos */}
          <div className="flex items-center justify-center gap-8 mb-4">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Almacenes Digitales" className="h-20 w-auto" />
              <div className="flex flex-col">
                <span className="text-gray-900 text-3xl font-bold tracking-tight leading-none">ALMACENES</span>
                <span className="text-blue-600 text-2xl font-medium tracking-tight leading-none">DIGITALES</span>
              </div>
            </div>
            <span className="text-gray-500 text-4xl font-thin">|</span>
            <img src={partner.logo} alt={partner.displayName} className={cn('h-32 w-auto', partner.logoClassName)} />
          </div>

          {/* Título principal */}
          <div className="flex-1 flex flex-col justify-center">
            <h1 className="mb-4 leading-tight">
              <span className="text-5xl font-bold text-gray-900">Almacenes </span>
              <span className="text-4xl font-medium text-gray-900">Digitales</span>
              <br />
              <span className="text-5xl font-bold text-gray-900">{partner.conjuncion} {partner.displayName},</span>
              <br />
              <span className="text-5xl font-bold text-blue-600">juntos por tu negocio.</span>
            </h1>
            <p className="text-xl text-gray-800 mb-6">
              Únete a la alianza y accede a herramientas y beneficios <span className="font-bold">100% gratuitos</span> para el canal tradicional.
            </p>

            {/* Beneficios */}
            <div className="bg-white rounded-3xl p-8 shadow-xl mb-6 relative pt-10">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-blue-900 text-white text-center py-3 px-8 rounded-full font-bold text-lg whitespace-nowrap">
                6 BENEFICIOS CLAVE
              </div>
              <div className="grid grid-cols-3 gap-0 divide-x divide-yellow-200">
                {BENEFICIOS.slice(0, 3).map((b) => (
                  <div key={b.n} className="flex items-center gap-3 px-4">
                    <div className="bg-blue-100 rounded-2xl p-3 flex items-center justify-center flex-shrink-0">
                      {b.icon}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-blue-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">{b.n}</span>
                      <div>
                        {b.lines.map((line) => (
                          <p key={line} className="text-sm font-bold text-gray-900 leading-tight">{line}</p>
                        ))}
                        {b.highlight && (
                          <p className="text-sm font-bold text-blue-600 leading-tight">{b.highlight}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Segunda fila */}
              <div className="h-px bg-yellow-200 my-4"></div>
              <div className="grid grid-cols-3 gap-0 divide-x divide-yellow-200">
                {BENEFICIOS.slice(3, 6).map((b) => (
                  <div key={b.n} className="flex items-center gap-3 px-4">
                    <div className="bg-blue-100 rounded-2xl p-3 flex items-center justify-center flex-shrink-0">
                      {b.icon}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-blue-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">{b.n}</span>
                      <div>
                        {b.lines.map((line) => (
                          <p key={line} className="text-sm font-bold text-gray-900 leading-tight">{line}</p>
                        ))}
                        {b.highlight && (
                          <p className="text-sm font-bold text-blue-600 leading-tight">{b.highlight}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-2 px-2">
              <p className="text-xs text-gray-600 italic">
                * Al registrarte con {partner.displayName} obtienes <span className="font-bold text-blue-600">{partner.monthsTrial} {mesesLabel} gratis</span> de la versión PRO. Después tiene un costo adicional. Los otros 5 beneficios son 100% gratuitos.
              </p>
            </div>

            {/* Footer con mensajes */}
            <div className="bg-blue-900 rounded-2xl p-6 text-white flex items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <svg className="w-12 h-12 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                <div>
                  <p className="font-bold text-lg"><span className="text-yellow-300">100% GRATIS</span> para el</p>
                  <p className="font-semibold">Canal Tradicional</p>
                </div>
              </div>
              <div className="border-l-2 border-white/30 pl-6">
                <div className="flex items-center gap-3">
                  <svg className="w-10 h-10 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <div>
                    <p className="font-semibold text-sm">Una alianza pensada</p>
                    <p className="text-sm">para ti, pensada para</p>
                    <p className="text-sm">tu comunidad.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Columna derecha - Formulario */}
        <div className="flex items-center justify-center px-8 py-6 h-full overflow-auto bg-white">
          <div className="w-full max-w-xl">
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-6">
              {/* Header del formulario */}
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-600 rounded-full p-3">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Únete a la alianza entre <span className="text-blue-600">Almacenes Digitales</span> {partner.conjuncion} <span className={nameClassName}>{partner.displayName}</span>
                  </h2>
                </div>
                <div className="flex items-center gap-2 text-blue-600 mb-4">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <p className="text-sm font-medium">
                    Completa tus datos y accede a todos los <span className="font-bold">beneficios gratuitos.</span>
                  </p>
                </div>
              </div>

              {apiError && (
                <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-800 border border-red-200">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p>{apiError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      RUT del negocio <span className="text-red-500">*</span>
                    </label>
                    <div className={`flex items-center border rounded-lg overflow-hidden ${errors.rutNegocio ? 'border-red-500' : 'border-gray-300'}`}>
                      <span className="px-3 text-gray-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                        </svg>
                      </span>
                      <input
                        type="text"
                        required
                        value={formattedRutNegocio}
                        onChange={(e) => {
                          handleRutNegocioChange(e);
                          setErrors({ ...errors, rutNegocio: '' });
                        }}
                        placeholder="Ej: 76.123.456-7"
                        className="flex-1 px-3 py-3 text-base outline-none"
                      />
                    </div>
                    {errors.rutNegocio && (
                      <p className="mt-1 text-sm text-red-600">{errors.rutNegocio}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      RUT del representante <span className="text-red-500">*</span>
                    </label>
                    <div className={`flex items-center border rounded-lg overflow-hidden ${errors.run ? 'border-red-500' : 'border-gray-300'}`}>
                      <span className="px-3 text-gray-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                        </svg>
                      </span>
                      <input
                        type="text"
                        required
                        value={formattedRun}
                        onChange={(e) => {
                          handleRunChange(e);
                          setErrors({ ...errors, run: '' });
                        }}
                        placeholder="Ej: 12.345.678-9"
                        className="flex-1 px-3 py-3 text-base outline-none"
                      />
                    </div>
                    {errors.run && (
                      <p className="mt-1 text-sm text-red-600">{errors.run}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Nombre <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                      <span className="px-3 text-gray-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                        </svg>
                      </span>
                      <input
                        type="text"
                        required
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder="Ej: Juan Pérez"
                        className="flex-1 px-3 py-3 text-base outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Apellidos <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                      <span className="px-3 text-gray-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                        </svg>
                      </span>
                      <input
                        type="text"
                        required
                        value={formData.apellidos}
                        onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                        placeholder="Ej: Pérez González"
                        className="flex-1 px-3 py-3 text-base outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Correo electrónico <span className="text-red-500">*</span>
                    </label>
                    <div className={`flex items-center border rounded-lg overflow-hidden ${errors.email ? 'border-red-500' : 'border-gray-300'}`}>
                      <span className="px-3 text-gray-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                        </svg>
                      </span>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          setErrors({ ...errors, email: '' });
                        }}
                        placeholder="Ej: contacto@tualmacen.cl"
                        className="flex-1 px-3 py-3 text-base outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Repite tu correo electrónico <span className="text-red-500">*</span>
                    </label>
                    <div className={`flex items-center border rounded-lg overflow-hidden ${errors.email ? 'border-red-500' : 'border-gray-300'}`}>
                      <span className="px-3 text-gray-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                        </svg>
                      </span>
                      <input
                        type="email"
                        required
                        value={formData.emailConfirm}
                        onChange={(e) => {
                          setFormData({ ...formData, emailConfirm: e.target.value });
                          setErrors({ ...errors, email: '' });
                        }}
                        placeholder="Ej: contacto@tualmacen.cl"
                        className="flex-1 px-3 py-3 text-base outline-none"
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Número de teléfono <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`flex items-center border rounded-lg overflow-hidden ${
                      errors.telefono ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <span className="px-3 text-gray-400">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                      </svg>
                    </span>
                    <span className="px-3 py-3 bg-gray-50 text-gray-600 text-base font-medium border-r border-gray-300">
                      +56
                    </span>
                    <input
                      type="tel"
                      required
                      value={formData.telefono}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                        setFormData({ ...formData, telefono: value });
                        setErrors({ ...errors, telefono: '' });
                      }}
                      placeholder="9 1234 5678"
                      className="flex-1 px-3 py-3 text-base outline-none"
                      maxLength={9}
                      pattern="[0-9]{9}"
                      title="El número debe tener exactamente 9 dígitos"
                    />
                  </div>
                  {errors.telefono && (
                    <p className="mt-1 text-sm text-red-600">{errors.telefono}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full py-4 text-lg font-bold"
                  size="lg"
                  isLoading={isPending}
                >
                  Quiero registrarme
                </Button>

                <div className="pt-4">
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-600 mb-4">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                    </svg>
                    <p>Al registrarte, aceptas formar parte de la alianza entre Almacenes Digitales {partner.conjuncion} {partner.displayName}.</p>
                  </div>
                  <div className="flex items-center justify-center gap-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <img src={logo} alt="Almacenes Digitales" className="h-10 w-auto" />
                      <div className="flex flex-col">
                        <span className="text-gray-900 text-sm font-bold leading-none">ALMACENES</span>
                        <span className="text-blue-600 text-sm font-bold leading-none">DIGITALES</span>
                      </div>
                    </div>
                    <span className="text-gray-300">|</span>
                    <img src={partner.logo} alt={partner.displayName} className={cn('h-14 w-auto', partner.logoFooterClassName)} />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
